import { useState } from "react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import ModuleShell from "@/components/ModuleShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ClipboardText, CaretDown, CaretRight } from "@phosphor-icons/react";

const CHECKUP_SECTIONS = [
  {
    title: "1. Presentazione azienda",
    items: [
      "Di cosa si occupa l'azienda?",
      "Numero dipendenti",
      "Numero postazioni",
      "Numero di stampanti",
      "Software principali utilizzati",
      "Sono presenti server, NAS o servizi cloud?",
    ],
  },
  {
    title: "2. Assistenza informatica",
    items: [
      "Chi gestisce oggi l'informatica aziendale?",
      "Da quanto tempo collaborate con lui?",
      "Quali sono i problemi più frequenti?",
      "Cosa vorreste migliorare?",
      "Assistenza a chiamata o contratto?",
    ],
  },
  {
    title: "3. Sicurezza informatica",
    items: [
      "Come proteggete oggi i vostri sistemi?",
      "Avete mai avuto problemi di virus, ransomware o phishing?",
      "Ci sono procedure di sicurezza particolari?",
      "Che antivirus usate? Avete un controllo remoto degli eventi?",
    ],
  },
  {
    title: "4. Backup e continuità operativa",
    items: [
      "Come vengono salvati i dati aziendali?",
      "Manutenzione attiva sui dischi di salvataggio?",
      "Sapete quanto tempo impieghereste a ripartire dopo un guasto importante?",
    ],
  },
  {
    title: "5. Rete e connettività",
    items: [
      "Come è strutturata la rete aziendale?",
      "È presente un firewall?",
      "Avete una piantina della rete?",
      "Ci sono problemi di Wi-Fi, connessione o lentezza?",
    ],
  },
  {
    title: "6. Email e cloud",
    items: [
      "Come gestite la posta elettronica?",
      "Utilizzate Microsoft 365, Google Workspace o altri servizi cloud?",
    ],
  },
  {
    title: "7. GDPR e privacy",
    items: [
      "Chi segue gli aspetti relativi al GDPR?",
      "Avete un elenco o gestione delle password?",
      "Ogni quanto cambiate le password?",
      "Sono già state svolte attività di adeguamento?",
    ],
  },
  {
    title: "8. Videosorveglianza",
    items: [
      "È presente un impianto di videosorveglianza?",
      "Ci sono criticità o progetti futuri?",
    ],
  },
  {
    title: "9. Digitalizzazione",
    items: [
      "Quali attività vengono ancora svolte manualmente?",
      "Ci sono processi che vorreste automatizzare?",
    ],
  },
  {
    title: "10. Intelligenza artificiale",
    items: [
      "L'azienda utilizza già strumenti di AI?",
      "Ci sono attività ripetitive che potrebbero essere automatizzate?",
    ],
  },
  {
    title: "11. Immagine aziendale",
    items: [
      "L'azienda dispone di sito web, materiale grafico e presenza online?",
      "Da chi è registrato e seguito il dominio?",
      "Sono previsti investimenti in comunicazione o marketing?",
    ],
  },
  {
    title: "12. Esigenze del cliente",
    items: [
      "Qual è oggi il problema informatico che vi crea più difficoltà?",
      "Quali sono gli obiettivi dell'azienda nei prossimi 12-24 mesi?",
    ],
  },
  {
    title: "13. Osservazioni del tecnico",
    items: [
      "Stato generale dell'infrastruttura",
      "Criticità riscontrate",
      "Opportunità di miglioramento",
      "Servizi Diotech potenzialmente proponibili",
      "Priorità degli interventi",
    ],
  },
];

export default function CheckupModule() {
  useDocumentTitle("Modulo · Check-up Iniziale");
  // answers[sectionIdx][itemIdx] = string
  const [answers, setAnswers] = useState({});
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(CHECKUP_SECTIONS.map((_, i) => [i, i < 3]))
  );

  const setAnswer = (si, ii, v) =>
    setAnswers((prev) => ({ ...prev, [`${si}-${ii}`]: v }));
  const getAnswer = (si, ii) => answers[`${si}-${ii}`] || "";

  const toggleSection = (i) => setOpenSections((s) => ({ ...s, [i]: !s[i] }));

  const buildPayload = (customerId) => ({
    customer_id: customerId,
    sections: CHECKUP_SECTIONS.map((sec, si) => ({
      title: sec.title,
      items: sec.items.map((q, ii) => ({ q, a: getAnswer(si, ii) || null })),
    })),
  });

  return (
    <ModuleShell
      title="Check-up Iniziale"
      subtitle="Scheda di analisi infrastruttura IT. Puoi anche stamparla in bianco e compilarla a mano."
      icon={ClipboardText}
      endpoint="/modules/checkup/pdf"
      filenamePrefix="checkup-iniziale"
      emailSubject={() => "Check-up Iniziale - Diotech"}
      emailBody={(c) => `Gentile ${c.surname} ${c.name},\n\nin allegato trova la scheda di check-up compilata durante la nostra visita.`}
      buildPayload={buildPayload}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
        // COMPILAZIONE CHECK-UP <span className="normal-case ml-2 text-muted-foreground/70">(tutti i campi opzionali)</span>
      </div>
      <div className="text-xs text-muted-foreground mb-4">
        Compila solo i campi che vuoi. I campi lasciati vuoti verranno stampati con una riga tratteggiata per essere completati a penna.
      </div>
      <div className="space-y-2">
        {CHECKUP_SECTIONS.map((sec, si) => (
          <Card key={si} className="border-border/70 rounded-lg overflow-hidden" data-testid={`checkup-section-${si}`}>
            <button
              type="button"
              onClick={() => toggleSection(si)}
              className="w-full flex items-center justify-between px-4 py-3 bg-secondary/40 hover:bg-secondary/60 transition-colors"
            >
              <div className="font-display font-bold tracking-tight text-sm text-left">{sec.title}</div>
              {openSections[si] ? <CaretDown size={16} /> : <CaretRight size={16} />}
            </button>
            {openSections[si] && (
              <div className="p-4 space-y-3">
                {sec.items.map((q, ii) => (
                  <div key={ii}>
                    <Label className="text-xs font-medium">{q}</Label>
                    <Textarea
                      rows={2}
                      value={getAnswer(si, ii)}
                      onChange={(e) => setAnswer(si, ii, e.target.value)}
                      placeholder="Lascia vuoto per compilare a mano"
                      className="mt-1 text-sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>
    </ModuleShell>
  );
}
